import {
  Box,
  Button,
  TextField,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useMutation, useQuery } from "react-query";
import { getStyles } from "src/styles/theme";
import defaultStyles from "./styles";
import SendIcon from "@mui/icons-material/Send";
import { useAppSelector } from "src/redux/store";
import { getTargetQueries, addTargetQuery } from "src/utils/api/dashboard";

interface TargetQuery {
  id: string;
  targetId: string;
  userId: string;
  status: string;
  query: string;
  created_at: string;
  updated_at: string;
  name?: string;
  role?: string;
  user?: {
    id: string;
    name: string;
    userType: string;
  };
}

interface TargetQueryHistoryModalProps {
  targetId: string;
  open: boolean;
  onClose: () => void;
}

export const TargetQueryHistoryModal = ({
  targetId,
  open,
  onClose,
}: TargetQueryHistoryModalProps) => {
  const styles = getStyles(defaultStyles);
  const [query, setQuery] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Get user from Redux store
  const user = useAppSelector((state) => state.root.user.data);
  console.log(user, "user data in query modal");
  const userId = user?.userId;
  const isApprover = user?.role === "Approver";

  // Fetch target queries
  const {
    data: queriesData,
    isLoading: isLoadingQueries,
    refetch,
    error: queryError,
  } = useQuery(
    ["targetQueries", targetId],
    async () => {
      try {
        const response = await getTargetQueries(targetId);
        const data = response?.data?.data || [];

        // Convert object with numeric keys to array
        if (data && typeof data === "object" && !Array.isArray(data)) {
          return Object.values(data);
        }

        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Error fetching queries:", error);
        return [];
      }
    },
    {
      enabled: open && !!targetId,
      refetchOnMount: true,
      onError: (error: any) => {
        console.error("Error fetching queries:", error);
      },
    },
  );

  console.log(queriesData, "queries data in modal");
  // Mutation to add new query
  const { mutate: submitQuery, isLoading: isAddingQuery } = useMutation(
    async () => {
      if (!userId) {
        throw new Error("User ID not found");
      }

      const payload = {
        targetId,
        userId,
        query,
      };

      console.log("Sending payload:", payload);

      const response = await addTargetQuery(payload);
      return response?.data;
    },
    {
      onSuccess: () => {
        setQuery("");
        setErrorMessage("");
        refetch();
      },
      onError: (error: any) => {
        const errorMsg =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to add query";
        setErrorMessage(errorMsg);
        console.error("Error adding query:", error);
      },
    },
  );

  const handleAddQuery = () => {
    if (!query.trim()) {
      setErrorMessage("Query cannot be empty");
      return;
    }
    if (!userId) {
      setErrorMessage("User information not available");
      return;
    }
    submitQuery();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && e.ctrlKey) {
      handleAddQuery();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "RESOLVED":
        return "#4caf50";
      case "PENDING":
        return "#ff9800";
      case "IN_PROGRESS":
        return "#2196f3";
      default:
        return "#757575";
    }
  };

  const getUserLabel = (userType?: string) => {
    if (!userType) return "USER";
    const typeMap: { [key: string]: string } = {
      ADMIN: "QUERY FROM ADMIN",
      NODAL_OFFICER: "TEXT FROM SNO",
      IMPLEMENTING_OFFICER: "REPLY FROM IO (IMPLEMENTING OFFICER)",
      LO: "QUERY FROM LAND OWNER",
    };
    return typeMap[userType.toUpperCase()] || "USER";
  };

  const getUserLabelColor = (userType?: string) => {
    const colorMap: { [key: string]: string } = {
      ADMIN: "#d32f2f",
      NODAL_OFFICER: "#f57c00",
      IMPLEMENTING_OFFFICER: "#388e3c",
      LO: "#1976d2",
    };
    return colorMap[userType?.toUpperCase() || ""] || "#757575";
  };

  return (
    <>
      <DialogTitle sx={{ fontWeight: "bold", fontSize: "18px" }}>
        Query History: {/* You can add target name here if needed */}
      </DialogTitle>
      <DialogContent sx={{ minHeight: "500px", maxHeight: "600px" }}>
        {/* Error Alert */}
        {errorMessage && (
          <Alert severity="error" sx={{ marginBottom: "12px" }}>
            {errorMessage}
          </Alert>
        )}

        {/* Queries Display Area */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            marginBottom: "20px",
            maxHeight: "350px",
            overflowY: "auto",
            paddingRight: "8px",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#888",
              borderRadius: "10px",
            },
          }}
        >
          {isLoadingQueries ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                padding: "20px",
              }}
            >
              <CircularProgress size={30} />
            </Box>
          ) : queriesData && queriesData.length > 0 ? (
            queriesData.map((item: any) => (
              <Paper
                key={item.id}
                sx={{
                  padding: "16px",
                  backgroundColor: item.name ? "#e8f5e9" : "#f5f5f5",
                  borderLeft: `4px solid ${getUserLabelColor(
                    item.userSubType || item.user?.userType,
                  )}`,
                  borderRadius: "4px",
                  marginBottom: "8px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "8px",
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: "bold",
                        color: "#333",
                        fontSize: "14px",
                      }}
                    >
                      {item.name || item.user?.name || "Unknown User"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: getUserLabelColor(
                          item.userSubType || item.user?.userType,
                        ),
                        fontSize: "11px",
                      }}
                    >
                      {item.userSubType || item.user?.userType || "User"}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: "#999" }}>
                    {formatDate(item.created_at)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      wordWrap: "break-word",
                      color: "#333",
                      lineHeight: "1.6",
                      fontSize: "14px",
                      marginTop: "8px",
                    }}
                  >
                    {item.query}
                  </Typography>
                  <Typography
                    sx={{
                      wordWrap: "break-word",
                      color: "#767676",
                      lineHeight: "1.6",
                      fontSize: "10px",
                      marginTop: "8px",
                    }}
                  >
                    {item.status}
                  </Typography>
                </Box>
              </Paper>
            ))
          ) : (
            <Alert severity="info">No queries found for this target.</Alert>
          )}
        </Box>

        {/* Add New Query Section */}
        {isApprover && (
          <Box sx={{ borderTop: "1px solid #ddd", paddingTop: "16px" }}>
            <Typography
              variant="subtitle2"
              sx={{ marginBottom: "12px", fontWeight: "bold" }}
            >
              Add Further Remarks:
            </Typography>

            <TextField
              fullWidth
              multiline
              placeholder="Type here..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              size="small"
              disabled={isAddingQuery}
              sx={{ marginBottom: "12px" }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: "16px" }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        {isApprover && (
          <Button
            onClick={handleAddQuery}
            variant="contained"
            disabled={!query.trim() || isAddingQuery || !userId}
            endIcon={
              isAddingQuery ? <CircularProgress size={20} /> : <SendIcon />
            }
          >
            Submit Remark
          </Button>
        )}
      </DialogActions>
    </>
  );
};
